// src/pages/UykuTakip.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import HotelIcon from '@mui/icons-material/Hotel';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom'; // Yönlendirme

import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';
import usePermissions from '../hooks/usePermissions'; // Yetki

const UykuTakip = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { canDelete } = usePermissions();

    useEffect(() => {
        const q = query(collection(db, "sleepAppointments"), orderBy("olusturmaTarihi", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setAppointments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Tamamlandı': return 'success';
            case 'İptal Edildi': return 'error';
            case 'Gelmedi': return 'default';
            case 'Planlandı':
            default:
                return 'warning';
        }
    };

    const handleAddClick = () => {
        navigate('/uyku-takip/yeni');
    };

    const handleEditClick = (id) => {
        navigate(`/uyku-takip/${id}`);
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) {
            try {
                await deleteDoc(doc(db, "sleepAppointments", id));
            } catch (error) {
                console.error("Silme hatası:", error);
                alert("Silinirken hata oluştu.");
            }
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate();
        return format(date, 'dd.MM.yyyy HH:mm', { locale: tr });
    };

    const formatDay = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate();
        return format(date, 'EEEE', { locale: tr });
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    const formatDateForExport = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return format(date, 'dd.MM.yyyy HH:mm', { locale: tr });
    };

    const handleExportCSV = () => {
        const headers = ['Tarih', 'Gün', 'Hasta Adı', 'Telefon', 'Fiyat', 'Doktoru', 'Hastane', 'Durum'];
        const rows = filteredAppointments.map(app => [
            formatDateForExport(app.randevuTarihi),
            app.randevuTarihi ? format(app.randevuTarihi.toDate(), 'EEEE', { locale: tr }) : '',
            app.musteriAdSoyad || '',
            app.musteriTelefon || '',
            app.fiyat || '',
            app.doktoru || '',
            app.hastane || '',
            app.durum || 'Planlandı'
        ]);

        const csvContent = [
            headers.join(';'),
            ...rows.map(row => row.join(';'))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `uyku_hastalari_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const filteredAppointments = appointments.filter(app =>
        (app.musteriAdSoyad || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (app.doktoru || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (app.hastane || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return <Loading message="Uyku takip randevuları yükleniyor..." />;
    }

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            <PageHeader
                title="Uyku Takip Randevuları"
                subtitle="Randevu ve takiplerinizi buradan yönetebilirsiniz."
                icon={<HotelIcon />}
            >
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Hasta, Doktor Ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
                            }}
                            sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                        />
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV}>
                            CSV İndir
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick} sx={{ px: 3 }}>Yeni Randevu</Button>
            </PageHeader>

            <GlassCard sx={{ width: '100%', overflow: 'hidden', p: 0 }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Tarih</TableCell>
                                <TableCell>Gün</TableCell>
                                <TableCell>Hasta Adı</TableCell>
                                <TableCell>Telefon</TableCell>
                                <TableCell>Fiyat</TableCell>
                                <TableCell>Doktoru</TableCell>
                                <TableCell>Hastane</TableCell>
                                <TableCell>Durum</TableCell>
                                <TableCell align="center">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAppointments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ border: 0 }}>
                                        <EmptyState
                                            icon={<HotelIcon />}
                                            message="Kayıt bulunamadı."
                                            description={searchTerm ? 'Aramanızla eşleşen kayıt yok.' : 'Yeni bir randevu ekleyerek başlayın.'}
                                        />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredAppointments.map((app) => (
                                    <TableRow
                                        hover
                                        key={app.id}
                                        onClick={() => handleEditClick(app.id)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{formatDate(app.randevuTarihi)}</TableCell>
                                        <TableCell>{formatDay(app.randevuTarihi)}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{app.musteriAdSoyad || '-'}</TableCell>
                                        <TableCell>{app.musteriTelefon || '-'}</TableCell>
                                        <TableCell>{formatCurrency(app.fiyat)}</TableCell>
                                        <TableCell>{app.doktoru || '-'}</TableCell>
                                        <TableCell>{app.hastane || '-'}</TableCell>
                                        <TableCell>
                                            <StatusChip
                                                label={app.durum || 'Planlandı'}
                                                color={getStatusColor(app.durum)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                <Tooltip title="Düzenle">
                                                    <IconButton aria-label="Düzenle" size="small" onClick={(e) => { e.stopPropagation(); handleEditClick(app.id); }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {canDelete() && (
                                                    <Tooltip title="Sil">
                                                        <IconButton aria-label="Sil" size="small" color="error" onClick={(e) => handleDelete(e, app.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </GlassCard>
        </Box>
    );
};

export default UykuTakip;

